import { EyeOutlined } from '@ant-design/icons';
import { Class, ClassesResponseDTO } from '@dto/architecture.dto';
import {
  ConfigurationChangeFilter,
  GetConfigurationChangeFilterRule,
} from '@dto/configurationChanges.dto';
import { GroupCustomGroupFoldersTreeDTO, GroupCustomGroupFolderTree } from '@dto/customGroup.dto';
import { Button, Form, ModalProps } from 'antd';
import axios, { AxiosError, AxiosResponse } from 'axios';
import { Fragment, useEffect, useState } from 'react';
import { XmButton } from 'src/component/button/XmButton';
import { XmModal } from "src/component/modal/XmModal";
import { makePreviewApiData } from 'src/component/selectItems/comp/preview/CgPreviewHandler';
import { usePreviewModal } from 'src/component/selectItems/comp/preview/Preview';
import { XmSpinner } from 'src/component/spinner/XmSpinner';
import { API_URL } from 'src/data/Api';
import { Log } from 'src/service/log.service';
import { GLOB } from 'src/util/Glob';
import FilterForm, { FormFilterInstance } from './Form';
type ApiData = [AxiosResponse<GroupCustomGroupFoldersTreeDTO>, AxiosResponse<ClassesResponseDTO>];

export interface PropsBase {
  onFormFinish: (filter: ConfigurationChangeFilter) => void;
  label: string;
  usedFilterLabels: string[];
  filterId: string | undefined;
  groupId: number;
  data: GetConfigurationChangeFilterRule[];
  isCreate?: boolean;
}

type ModalWrapperProps = ModalProps &
  PropsBase & {
    onOk: () => void;
  };

const ModalWrapper = ({ filterId, ...rest }: ModalWrapperProps) => {
  const [classOptions, setClassOptions] = useState<Class[]>();
  const [folders, setFolders] = useState<GroupCustomGroupFolderTree[]>();
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const controller = new AbortController();
    setIsLoading(true);
    Promise.all([
      axios.get<GroupCustomGroupFoldersTreeDTO>(API_URL.CUSTOM_GROUP + '/folders/groups', {
        signal: controller.signal,
      }),
      axios.get<ClassesResponseDTO>(API_URL.ARCHITECTURE + '/classes?type=tracker', {
        signal: controller.signal,
      }),
    ])
      .then(processData, (reason: AxiosError) => Log.error('Failed to get filter data!', reason))
      .finally(() => setIsLoading(false));

    function processData([
      {
        data: { data: groupFolders },
      },
      {
        data: { data: classes },
      },
    ]: ApiData) {
      setClassOptions(classes);
      setFolders(groupFolders);
    }

    return () => controller.abort();
  }, []);

  // key => create new form instance
  return (
    <Fragment key={`${filterId}-${rest.open}`}>
      <FilterModal
        {...rest}
        filterId={filterId}
        classOptions={classOptions}
        isLoadingClassAndFolders={isLoading}
        folders={folders}
      />
    </Fragment>
  );
};

export type FilterModalProps = PropsBase &
  ModalProps & {
    folders: GroupCustomGroupFolderTree[];
    classOptions: Class[];
    isLoadingClassAndFolders: boolean;
    onOk: () => void;
  };

const FilterModal = ({
  onFormFinish,
  label,
  isCreate,
  data,
  classOptions,
  isLoadingClassAndFolders,
  usedFilterLabels,
  filterId,
  groupId,
  folders,
  ...modalProps
}: FilterModalProps) => {
  const [form] = Form.useForm<FormFilterInstance>();
  const [isLoading, setIsLoading] = useState(false);
  const handleOnOk = (_: React.MouseEvent<HTMLElement>) => {
    setIsLoading(true);
    void form
      .validateFields()
      .then(() => {
        form.submit();
      })
      .finally(() => setIsLoading(false));
  };

  const handleFormFinished = (filter: ConfigurationChangeFilter) => {
    setIsLoading(false);
    onFormFinish(filter);
    modalProps.onOk();
  };

  const handleCancel = (e: React.MouseEvent<HTMLButtonElement>) => {
    setIsLoading(false);
    form.resetFields();
    modalProps.onCancel?.(e);
  };

  //ModalHook
  const [showPreview, contextHolderRegex] = usePreviewModal();
  const [isLoadingMultiPreview, setIsLoadingMultiPreview] = useState(false);

  const handleMultiPreview = () => {
    setIsLoadingMultiPreview(true);
    void form
      .validateFields()
      .then((e) => {
        const tmpArrayOfRules = e?.rules?.map((rule) => ({
          ...makePreviewApiData(
            {
              cls: rule.class,
              hwType: rule?.hw_type,
              subsystem: rule.subsystem,
              parents: rule?.parents,
              regex: rule?.regex,
              tags: rule?.tags,
              exclude: rule?.exclude,
              items: rule?.items
            },
            false
          )
        }));
        showPreview({ data: tmpArrayOfRules, subsystem: 'Items' });
      })
      .finally(() => setIsLoadingMultiPreview(false));
  };

  const { readonly } = GLOB.userInfo;

  return (
    <XmModal
      {...modalProps}
      className='xm-filter-modal'
      width={'55em'}
      onOk={handleOnOk}
      onCancel={handleCancel}
      title={readonly ? "View Filter" : (isCreate ? 'Create Filter' : 'Edit Filter')}
      mask={XmModal.MASK_NOT_CLOSABLE}
      confirmLoading={isLoading}
      footer={
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <div>
            <Button key="preview" icon={<EyeOutlined />} loading={isLoadingMultiPreview} onClick={handleMultiPreview}>
              View All Items
            </Button>
          </div>
          <div>
            <Button key="back" onClick={handleCancel}>
              Cancel
            </Button>
            <XmButton key="submit" type="primary" onClick={handleOnOk}>
              OK
            </XmButton>
          </div>
        </div>
      }
    >
      {isLoading && <XmSpinner />}
      {contextHolderRegex}
      <FilterForm
        label={label}
        data={data}
        form={form}
        filterId={filterId}
        groupId={groupId}
        isCreate={isCreate}
        onFormFinish={handleFormFinished}
        folders={folders}
        classOptions={classOptions}
        usedFilterLabels={usedFilterLabels}
        isLoadingClassAndFolders={isLoadingClassAndFolders}
      />
    </XmModal>
  );
};

export default ModalWrapper;
